import { SelectChangeEvent, MenuItem, Box, Divider } from '@mui/material';
import SweepSelect from '../SweepSelect';
import useCrmOrgPermissions from '../permissions/useCrmOrgPermissions';
import { Typography, colors } from '@sweep-io/sweep-design';
import { UserDefinedRoleGroupsInterface } from '@server/role-group-interface';
import { sortRoles } from '../../dashboard/role-manager/utils';
import { useState } from 'react';
import { useSweepRoleGroups } from '../useSweepRoleGroups';
import { ClampedText } from '../ClampedText';
import { TruncatedTextTooltip } from '../TruncatedTextTooltip';

interface RolesDropdownProps {
  userId?: string;
  userRole?: UserDefinedRoleGroupsInterface;
  accountRoles: UserDefinedRoleGroupsInterface[];
  onChangeRoleCb: (userId: string, roleId: string) => void;
  onUserRemove?: (userId: string) => void;
  disableRoleDropdown?: boolean;
  showBorders?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  withStaticWidth?: boolean;
}

export const RolesDropdown = ({
  userId,
  userRole,
  accountRoles,
  onChangeRoleCb,
  disableRoleDropdown,
  showBorders,
  onUserRemove,
  onOpen,
  onClose,
  withStaticWidth = false,
}: RolesDropdownProps) => {
  const { getUserRole } = useSweepRoleGroups();
  const [isAllowedBtn, isAllowedBtnDelete] = useCrmOrgPermissions([
    'edit:users:roles',
    'delete:users',
  ]);
  const [_userRole, setUserRole] = useState(userRole);

  if (disableRoleDropdown || !isAllowedBtn) {
    return (
      <Box sx={{ paddingRight: 1 }}>
        <Typography ellipsis variant="body">
          {userRole?.name}
        </Typography>
      </Box>
    );
  }

  const sorted = sortRoles(accountRoles);

  return (
    <SweepSelect
      removeBorders={!showBorders}
      SelectProps={{
        renderValue: (value) => (
          <span>
            <TruncatedTextTooltip title={getUserRole(value as string)?.name}>
              <Typography variant="body">{getUserRole(value as string)?.name}</Typography>
            </TruncatedTextTooltip>
          </span>
        ),
        value: _userRole?.id ?? '',
        onChange: (event: SelectChangeEvent<unknown>) => {
          event.preventDefault();
          const roleId = event.target.value as string;
          const role = accountRoles.find((role) => role.id === roleId);
          onChangeRoleCb(userId || '', roleId);
          role && setUserRole(role);
        },
        onOpen: onOpen,
        onClose: onClose,
        MenuProps: {
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        },
        sx: {
          minHeight: '32px',
          maxHeight: '440px',
          width: withStaticWidth ? '160px' : undefined,
          maxWidth: '160px',
          overflow: 'hidden',
          '& .MuiSelect-select': {
            paddingRight: 3,
            padding: '4px 12px',
          },
        },
      }}
    >
      {sorted.map((role) => (
        <MenuItem
          key={role.id}
          value={role.id}
          selected={_userRole?.id === role.id}
          sx={{
            p: 1,
            pl: 1.5,
            pr: 1.5,
            width: '312px',
            '&.Mui-selected': {
              backgroundColor: colors.grey[100],
              '&:hover': {
                backgroundColor: colors.grey[100],
              },
              '&:after': {
                backgroundImage: `url('data:image/svg+xml;utf8,<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.5 4.5L6.5 11.5L3 8" stroke="${encodeURIComponent(
                  colors.grey[900],
                )}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>')`,
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              minWidth: 0,
            }}
          >
            <Typography variant={_userRole?.id === role.id ? 'body-medium' : 'body'}>
              {role.name}
            </Typography>
            <ClampedText
              text={role.description}
              typographyVariant="caption"
              textColor={colors.grey[700]}
            />
          </Box>
        </MenuItem>
      ))}

      {isAllowedBtnDelete && onUserRemove && userId && (
        <>
          <Divider />
          <MenuItem
            value="remove_user"
            sx={{
              background: colors.white,
              color: colors.blush[500],
              p: 1,
              mt: 0.5,
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onUserRemove && onUserRemove(userId);
            }}
          >
            Delete
          </MenuItem>
        </>
      )}
    </SweepSelect>
  );
};
